/** @jsxRuntime classic */
/** @jsx jsx */

import React, { useMemo, useState } from 'react';
import { useTheme, jsx } from '@emotion/react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Droppable } from '../Droppable';
import { useMiddlewareContext } from '../../hooks/useMiddlewareContext';
import { WeeklyDraggable } from '../weekly/WeeklyDraggable';
import { MoreSlotsButton } from '../MoreSlotsButton';
import { MoreSlotsList } from '../MoreSlotsList';
import { LoadingContainer } from '../LoadingContainer';
import { ECalendarMode, IWeekToDropProps } from '../../types';
import {
	monthlyDateContainer,
	monthlyViewContainer,
	spaceWidth,
	weekDayContainer,
	weekDayEventContainer,
} from '../styles';
import { ETimes } from '../../../types/enums/times';
import { useResourceSlots } from '../../hooks/useResourceSlots';
import { ISlot } from '../../../organisms/bo_calendar_slot/types';
import { groupBy, sortBy } from 'lodash';
import { getCombinedDateTimeString } from '../../../lib/timeUtils';
import { DateTimeFormats } from '@bondsports/date-time';

dayjs.extend(isoWeek);

export const MonthlyView = ({ resourceId, EventComponent, isLoading }: IWeekToDropProps) => {
	const { slots } = useResourceSlots({ resourceId: Number(resourceId) });

	const { options } = useMiddlewareContext();
	const { date: optionsDate, isSundayFirstDay } = options;
	const dayShift = isSundayFirstDay ? 1 : 0;
	const today = dayjs.utc(optionsDate);
	const currentMonth = today.month();

	const { dates, withSixth } = useMemo(() => {
		let result = [] as dayjs.Dayjs[];
		let withSixth = true;

		for (let i = 1; i < 7 * 6 + 1; i++) {
			const date = today.set(ETimes.DATE, 1).isoWeekday(i - dayShift);

			// dont render other days if 1st day of 6th week is next month
			if (i === 36 && currentMonth !== date.month()) {
				withSixth = false;
				break;
			}

			result.push(date);
		}

		return { dates: result, withSixth };
	}, [dayShift, optionsDate]);

	let maxVisibleSlotsPerDay: number = 1;
	const todayContainer = document.getElementById('today-container');
	if (todayContainer) {
		const height: number = todayContainer.clientHeight - 20;
		maxVisibleSlotsPerDay = Math.floor(height / 18);
	}

	const slotsByDay = useMemo(() => {
		return groupBy(
			sortBy(slots, slot => getCombinedDateTimeString(slot.startDate, slot.startTime)),
			slot => slot.startDate
		);
	}, [slots]);

	return (
		<div css={monthlyViewContainer} data-aid="MonthlyView-monthly">
			{dates.map((weekDay, index) => (
				<WeekDayView
					key={`weekDayView-${index}`}
					mode={options.mode!}
					weekDay={weekDay}
					index={index}
					EventComponent={EventComponent}
					resourceId={resourceId}
					isLoading={isLoading}
					currentDate={options.date!}
					maxVisibleSlotsPerDay={maxVisibleSlotsPerDay}
					today={today}
					withSixth={withSixth}
					slots={slotsByDay[weekDay.format(DateTimeFormats.YYYY_MM_DD)] || []}
				/>
			))}
		</div>
	);
};

type WeekDayViewProps = {
	resourceId: string;
	EventComponent: React.FC<any>;
	isLoading?: boolean;
	weekDay: dayjs.Dayjs;
	index: number;
	maxVisibleSlotsPerDay: number;
	today: dayjs.Dayjs;
	mode: ECalendarMode;
	currentDate: string;
	withSixth?: boolean;
	slots: ISlot[];
};

export const WeekDayView: React.FC<WeekDayViewProps> = ({
	weekDay,
	resourceId,
	EventComponent,
	isLoading,
	index,
	maxVisibleSlotsPerDay,
	today,
	mode,
	currentDate,
	withSixth,
	slots,
}) => {
	const colors = useTheme();

	const [showModal, setShowModal] = useState(false);

	const droppableId: string = `${resourceId}_${weekDay.toISOString()}`;
	const withModal: boolean = slots.length > maxVisibleSlotsPerDay;

	const moreItemsCount: number = slots.length - (maxVisibleSlotsPerDay - 1);
	const isCurrentMonth: boolean = today.isSame(weekDay, ETimes.MONTH);
	const isCurrentDay: boolean = today.isSame(weekDay, ETimes.DATE);
	const isSeventh: boolean = (index + 1) % 7 === 0;

	return (
		<Droppable
			weekly
			dragging={false}
			id={droppableId}
			key={droppableId + index}
			height={`calc(100% / ${withSixth ? 6 : 5})`}
			maxWidth={`max(100% / 7, ${spaceWidth}px )`}
		>
			<div css={monthlyDateContainer(colors, isCurrentMonth, isCurrentDay, isSeventh)}>
				<div className="date">{weekDay.format('DD')}</div>
				<div
					id={isCurrentDay ? 'today-container' : undefined}
					css={weekDayContainer(colors, undefined, 'calc(100% - 16px)')}
				>
					{isLoading ? (
						<LoadingContainer />
					) : (
						slots
							.filter((_, i) => i < maxVisibleSlotsPerDay)
							.map((slot: ISlot) => (
								<WeeklyDraggable parentId={droppableId} state={slot} key={slot.id} id={slot.id}>
									<div css={weekDayEventContainer} key={slot.id}>
										<EventComponent
											event={slot}
											key={`EventComponent-${slot.id}`}
											mode={mode}
											currentDate={currentDate}
										/>
									</div>
								</WeeklyDraggable>
							))
					)}
					{withModal && (
						<MoreSlotsButton
							isWeekly
							colors={colors}
							handleToggleModal={() => setShowModal(prev => !prev)}
							moreItemsCount={moreItemsCount}
						/>
					)}
					{withModal && showModal && (
						<MoreSlotsList
							colors={colors}
							EventComponent={EventComponent}
							group={{ items: slots }}
							handleToggleModal={() => setShowModal(prev => !prev)}
							index={index}
							currentDate={currentDate}
						/>
					)}
				</div>
			</div>
		</Droppable>
	);
};
